// Milestone dates (CR-016; PLAN D18, S10): per opening, each date its own event in the dated closure form of D0's
// timeline (Accepted · Background check · HRIS hire · Start · Filled), and the three closeout clocks in active days.
// "Capture accepted, background check, HRIS hire, start and filled separately." A missing date is a station not yet
// reached, never an inferred one.

import { Card, KeyValue, MilestoneTimeline, Stack, STAGES_TEMPLATES, formatDate, type KeyValueItem, type MilestoneStation } from "@/mc";
import { CLOCK_LABELS, CLOSURE_LABELS, DATE_LABELS } from "./labels";

import type { ClockFigure, OpeningDates } from "./types";

const CLOCK_WORDS: Record<string, string> = {
  time_to_fill: CLOCK_LABELS.timeToFill,
  acceptance_to_hris: CLOCK_LABELS.acceptanceToHris,
  acceptance_to_start: CLOCK_LABELS.acceptanceToStart,
};

/** The five stations of one opening, done where a date is recorded and the first undated one active. */
export function stationsFor(dates: OpeningDates, filledAt: string | null | undefined): MilestoneStation[] {
  const marks: [string, string | null | undefined][] = [
    [DATE_LABELS.accepted, dates.accepted_at],
    [DATE_LABELS.backgroundCheck, dates.background_check_at],
    [DATE_LABELS.hrisHire, dates.hris_hired_at],
    [DATE_LABELS.start, dates.started_at ?? dates.start_date],
    [DATE_LABELS.filled, filledAt],
  ];
  const firstOpen = marks.findIndex(([, at]) => !at);
  return marks.map(([label, at], index) => ({
    label,
    state: at ? "done" : index === firstOpen ? "active" : "next",
    date: at ? formatDate(at) : undefined,
  }));
}

function clockValue(clock: ClockFigure): string {
  if (clock.active_days == null) return clock.unavailable_reason ?? "—";
  return STAGES_TEMPLATES.activeDays({ n: clock.active_days });
}

export function MilestoneDates({ dates, filledAt, clocks }: {
  dates: readonly OpeningDates[]; filledAt: string | null | undefined; clocks: readonly ClockFigure[];
}) {
  const items: KeyValueItem[] = clocks.map((clock) => [CLOCK_WORDS[clock.key] ?? clock.label, clockValue(clock)]);
  return (
    <Card title={CLOSURE_LABELS.milestoneDates}>
      <Stack>
        {dates.map((opening) => (
          <MilestoneTimeline key={`${opening.opening_index}-${opening.application_id}`} milestones
            label={CLOSURE_LABELS.milestoneDates} stations={stationsFor(opening, filledAt)} />
        ))}
        {items.length ? <KeyValue cols={3} items={items} /> : null}
      </Stack>
    </Card>
  );
}
